import { useCallback, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { getValidAuthHeadersOrThrow } from '../lib/auth'
import {
  fetchShopifyLocationMappings,
  resolveWarehouseLocation,
} from '../utils/locationInventory'
import type {
  ConfirmReturnItemInput,
  ConfirmReturnResult,
  ReturnLineItem,
  ReturnLookupCandidate,
  ReturnLookupResult,
  ReturnMatchKind,
  ShopifyPushSummary,
} from '../types/returns'

interface ShopifyOrderRow {
  shopify_order_id: number
  order_number: string
  customer_name: string | null
  customer_email: string | null
  tracking_code: string | null
  fulfillment_status: string | null
  line_items: Array<{
    sku?: string | null
    title?: string | null
    quantity?: number
    price?: string | number | null
    variant_id?: string | number | null
    product_id?: string | number | null
  }> | null
}

/**
 * Row shape of the lookup_order_for_return RPC.
 *
 * shopify_orders has RLS enabled with no policies by design, so it cannot be
 * read from the browser at all: such a read returns zero rows and no error.
 * The RPC is the sanctioned way through that boundary.
 */
interface LookupRow {
  match_kind: ReturnMatchKind
  shipping_label_id: string | null
  scanned_barcode: string | null
  shopify_order_id: number
  order_number: string
  customer_name: string | null
  customer_email: string | null
  tracking_code: string | null
  fulfillment_status: string | null
  order_date: string | null
  line_items: ShopifyOrderRow['line_items']
}

interface ProductRow {
  id: string
  sku: string
  name: string
  image_url: string | null
  shopify_variant_id: string | number | null
}

function normalizeSku(sku: string | null | undefined): string | null {
  if (!sku) return null
  const trimmed = sku.trim()
  if (!trimmed) return null
  return trimmed.toLowerCase().replace(/\s+/g, ' ')
}

function emptyLookup(scannedValue: string, status: ReturnLookupResult['status'], message?: string): ReturnLookupResult {
  return {
    status,
    message,
    scannedValue,
    shippingLabelId: null,
    scannedBarcode: null,
    shopifyOrderId: null,
    orderNumber: null,
    customerName: null,
    customerEmail: null,
    fulfillmentStatus: null,
    candidates: [],
    lines: [],
  }
}

function toCandidate(row: LookupRow): ReturnLookupCandidate {
  return {
    matchKind: row.match_kind,
    shippingLabelId: row.shipping_label_id,
    scannedBarcode: row.scanned_barcode,
    shopifyOrderId: Number(row.shopify_order_id),
    orderNumber: row.order_number,
    customerName: row.customer_name,
    customerEmail: row.customer_email,
    trackingCode: row.tracking_code,
    fulfillmentStatus: row.fulfillment_status,
    orderDate: row.order_date,
    itemCount: Array.isArray(row.line_items) ? row.line_items.length : 0,
  }
}

/** Only orders that never shipped are refused; the RPC enforces the same rule. */
function isNotReturnable(status: string | null): boolean {
  return (status || '').trim().toUpperCase() === 'UNFULFILLED'
}

async function loadPriorReturnedBySku(
  adminId: string,
  shopifyOrderId: number
): Promise<Map<string, number>> {
  const map = new Map<string, number>()
  const { data: returns, error } = await supabase
    .from('order_returns')
    .select('id')
    .eq('admin_id', adminId)
    .eq('shopify_order_id', shopifyOrderId)

  if (error || !returns || returns.length === 0) return map

  const returnIds = returns.map((r) => r.id)
  const { data: items } = await supabase
    .from('order_return_items')
    .select('sku, quantity')
    .in('return_id', returnIds)

  for (const item of items || []) {
    const key = normalizeSku(item.sku)
    if (!key) continue
    map.set(key, (map.get(key) || 0) + (item.quantity || 0))
  }
  return map
}

async function resolveLines(
  adminId: string,
  order: ShopifyOrderRow
): Promise<ReturnLineItem[]> {
  const rawItems = Array.isArray(order.line_items) ? order.line_items : []
  const priorBySku = await loadPriorReturnedBySku(adminId, order.shopify_order_id)

  const skus = Array.from(
    new Set(
      rawItems
        .map((li) => (li.sku || '').trim())
        .filter((s) => s.length > 0)
    )
  )

  const productsByExactSku = new Map<string, ProductRow[]>()
  const productsByNormSku = new Map<string, ProductRow[]>()
  const productsByVariant = new Map<string, ProductRow>()

  if (skus.length > 0) {
    const { data: exactRows } = await supabase
      .from('products')
      .select('id, sku, name, image_url, shopify_variant_id')
      .eq('admin_id', adminId)
      .in('sku', skus)

    for (const p of (exactRows || []) as ProductRow[]) {
      const list = productsByExactSku.get(p.sku) || []
      list.push(p)
      productsByExactSku.set(p.sku, list)
      const norm = normalizeSku(p.sku)
      if (norm) {
        const nList = productsByNormSku.get(norm) || []
        nList.push(p)
        productsByNormSku.set(norm, nList)
      }
    }

    const missingSkus = skus.filter((s) => !productsByExactSku.has(s))
    if (missingSkus.length > 0) {
      const { data: ciRows } = await supabase.rpc('get_products_by_skus_case_insensitive', {
        p_admin_id: adminId,
        p_skus: missingSkus,
      })
      const ciIds = Array.from(
        new Set((ciRows || []).map((r: { id: string }) => r.id).filter(Boolean))
      )
      if (ciIds.length > 0) {
        const { data: fullRows } = await supabase
          .from('products')
          .select('id, sku, name, image_url, shopify_variant_id')
          .eq('admin_id', adminId)
          .in('id', ciIds)
        for (const p of (fullRows || []) as ProductRow[]) {
          const norm = normalizeSku(p.sku)
          if (!norm) continue
          const nList = productsByNormSku.get(norm) || []
          if (!nList.some((x) => x.id === p.id)) nList.push(p)
          productsByNormSku.set(norm, nList)
        }
      }
    }
  }

  const variantIds = Array.from(
    new Set(
      rawItems
        .map((li) => (li.variant_id != null ? String(li.variant_id) : ''))
        .filter((v) => v.length > 0)
    )
  )
  if (variantIds.length > 0) {
    const { data: byVariant } = await supabase
      .from('products')
      .select('id, sku, name, image_url, shopify_variant_id')
      .eq('admin_id', adminId)
      .in('shopify_variant_id', variantIds)
    for (const p of (byVariant || []) as ProductRow[]) {
      if (p.shopify_variant_id != null)
        productsByVariant.set(String(p.shopify_variant_id), p)
    }
  }

  // Aggregate sold qty by normalized SKU (or variant when no SKU)
  const soldByKey = new Map<string, {
    sku: string
    title: string
    soldQuantity: number
    unitPrice: number | null
    variantId: string | null
    shopifyProductId: string | null
  }>()

  rawItems.forEach((li, idx) => {
    const sku = (li.sku || '').trim()
    const variantId = li.variant_id != null ? String(li.variant_id) : null
    const key = normalizeSku(sku) || (variantId ? `variant:${variantId}` : `line:${idx}`)
    const qty = Math.max(0, Number(li.quantity) || 0)
    const existing = soldByKey.get(key)
    const unitPrice = li.price != null && li.price !== '' ? Number(li.price) : null
    if (existing) {
      existing.soldQuantity += qty
      if (!existing.title && li.title) existing.title = li.title
    } else {
      soldByKey.set(key, {
        sku,
        title: li.title || sku || 'Unknown item',
        soldQuantity: qty,
        unitPrice: Number.isFinite(unitPrice as number) ? unitPrice : null,
        variantId,
        shopifyProductId: li.product_id != null ? String(li.product_id) : null,
      })
    }
  })

  const lines: ReturnLineItem[] = []
  let lineIndex = 0
  for (const [key, sold] of soldByKey) {
    lineIndex += 1
    let productId: string | null = null
    let productName: string | null = null
    let imageUrl: string | null = null
    let resolution: ReturnLineItem['resolution'] = 'unresolved'

    const exactMatches = sold.sku ? productsByExactSku.get(sold.sku) || [] : []
    const norm = normalizeSku(sold.sku)
    const normMatches = norm ? productsByNormSku.get(norm) || [] : []
    const matches = exactMatches.length > 0 ? exactMatches : normMatches

    if (matches.length === 1) {
      productId = matches[0].id
      productName = matches[0].name
      imageUrl = matches[0].image_url
      resolution = 'sku'
    } else if (matches.length > 1) {
      resolution = 'ambiguous'
    } else if (sold.variantId && productsByVariant.has(sold.variantId)) {
      const p = productsByVariant.get(sold.variantId)!
      productId = p.id
      productName = p.name
      imageUrl = p.image_url
      resolution = 'variant'
      if (!sold.sku && p.sku) sold.sku = p.sku
    }

    const alreadyReturned = norm ? (priorBySku.get(norm) || 0) : 0
    const remaining = Math.max(0, sold.soldQuantity - alreadyReturned)

    lines.push({
      lineKey: `${key}:${lineIndex}`,
      sku: sold.sku,
      title: sold.title,
      soldQuantity: sold.soldQuantity,
      alreadyReturned,
      remaining,
      unitPrice: sold.unitPrice,
      variantId: sold.variantId,
      shopifyProductId: sold.shopifyProductId,
      productId,
      productName,
      imageUrl,
      resolution,
    })
  }

  return lines
}

async function fetchLookupRows(adminId: string, value: string): Promise<LookupRow[]> {
  const { data, error } = await supabase.rpc('lookup_order_for_return', {
    p_admin_id: adminId,
    p_value: value,
  })

  // Surfaced, never swallowed: a failed lookup must not read as "does not exist".
  if (error) throw new Error(error.message)
  return (data || []) as LookupRow[]
}

export function useReturnShipment(adminId: string | null) {
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [shopifySync, setShopifySync] = useState<{ done: number; total: number } | null>(null)
  const [lookup, setLookup] = useState<ReturnLookupResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const shopifySyncChainRef = useRef<Promise<unknown>>(Promise.resolve())

  const clearLookup = useCallback(() => {
    setLookup(null)
    setError(null)
  }, [])

  const lookupValue = useCallback(async (rawValue: string): Promise<ReturnLookupResult> => {
    if (!adminId) {
      const result = emptyLookup(rawValue, 'empty', 'Unable to resolve tenant')
      setLookup(result)
      return result
    }

    const scannedValue = rawValue.trim()
    if (!scannedValue) {
      const result = emptyLookup(scannedValue, 'empty', 'Enter an order number or scan a label barcode')
      setLookup(result)
      return result
    }

    setLoading(true)
    setError(null)

    try {
      const rows = await fetchLookupRows(adminId, scannedValue)

      if (rows.length === 0) {
        const result = emptyLookup(
          scannedValue,
          'order_not_found',
          'No order or shipment label matches this value'
        )
        setLookup(result)
        return result
      }

      if (rows.length > 1) {
        const result: ReturnLookupResult = {
          ...emptyLookup(scannedValue, 'multiple_matches'),
          message: `${rows.length} orders match "${scannedValue}". Pick the right one.`,
          candidates: rows.map(toCandidate),
        }
        setLookup(result)
        return result
      }

      const row = rows[0]

      if (isNotReturnable(row.fulfillment_status)) {
        const result: ReturnLookupResult = {
          ...emptyLookup(
            scannedValue,
            'not_returnable',
            `Order ${row.order_number} was never fulfilled, so there is nothing to return.`
          ),
          shippingLabelId: row.shipping_label_id,
          scannedBarcode: row.scanned_barcode,
          shopifyOrderId: Number(row.shopify_order_id),
          orderNumber: row.order_number,
          customerName: row.customer_name,
          customerEmail: row.customer_email,
          fulfillmentStatus: row.fulfillment_status,
        }
        setLookup(result)
        return result
      }

      const lines = await resolveLines(adminId, {
        shopify_order_id: Number(row.shopify_order_id),
        order_number: row.order_number,
        customer_name: row.customer_name,
        customer_email: row.customer_email,
        tracking_code: row.tracking_code,
        fulfillment_status: row.fulfillment_status,
        line_items: row.line_items,
      })

      const result: ReturnLookupResult = {
        status: 'found',
        scannedValue,
        shippingLabelId: row.shipping_label_id,
        scannedBarcode: row.scanned_barcode,
        shopifyOrderId: Number(row.shopify_order_id),
        orderNumber: row.order_number,
        customerName: row.customer_name,
        customerEmail: row.customer_email,
        fulfillmentStatus: row.fulfillment_status,
        candidates: [],
        lines,
      }
      setLookup(result)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Lookup failed'
      setError(message)
      const result = emptyLookup(scannedValue, 'lookup_failed', message)
      setLookup(result)
      return result
    } finally {
      setLoading(false)
    }
  }, [adminId])

  /** Load one order chosen from the ambiguous-match picker. */
  const selectCandidate = useCallback(async (
    candidate: ReturnLookupCandidate
  ): Promise<ReturnLookupResult> => {
    if (!adminId) {
      const result = emptyLookup(candidate.orderNumber, 'empty', 'Unable to resolve tenant')
      setLookup(result)
      return result
    }

    setLoading(true)
    setError(null)

    try {
      // Re-resolve by exact order number so line_items come from one code path.
      const rows = await fetchLookupRows(adminId, candidate.orderNumber)
      const row = rows.find((r) => Number(r.shopify_order_id) === candidate.shopifyOrderId)

      if (!row) {
        const result = emptyLookup(
          candidate.orderNumber,
          'order_not_found',
          `Order ${candidate.orderNumber} could no longer be loaded`
        )
        setLookup(result)
        return result
      }

      if (isNotReturnable(row.fulfillment_status)) {
        const result: ReturnLookupResult = {
          ...emptyLookup(
            candidate.orderNumber,
            'not_returnable',
            `Order ${row.order_number} was never fulfilled, so there is nothing to return.`
          ),
          shopifyOrderId: Number(row.shopify_order_id),
          orderNumber: row.order_number,
          customerName: row.customer_name,
          fulfillmentStatus: row.fulfillment_status,
        }
        setLookup(result)
        return result
      }

      const lines = await resolveLines(adminId, {
        shopify_order_id: Number(row.shopify_order_id),
        order_number: row.order_number,
        customer_name: row.customer_name,
        customer_email: row.customer_email,
        tracking_code: row.tracking_code,
        fulfillment_status: row.fulfillment_status,
        line_items: row.line_items,
      })

      const result: ReturnLookupResult = {
        status: 'found',
        scannedValue: candidate.orderNumber,
        shippingLabelId: candidate.shippingLabelId ?? row.shipping_label_id,
        scannedBarcode: candidate.scannedBarcode ?? row.scanned_barcode,
        shopifyOrderId: Number(row.shopify_order_id),
        orderNumber: row.order_number,
        customerName: row.customer_name,
        customerEmail: row.customer_email,
        fulfillmentStatus: row.fulfillment_status,
        candidates: [],
        lines,
      }
      setLookup(result)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Lookup failed'
      setError(message)
      const result = emptyLookup(candidate.orderNumber, 'lookup_failed', message)
      setLookup(result)
      return result
    } finally {
      setLoading(false)
    }
  }, [adminId])

  const pushWarehouseToShopify = useCallback(async (
    productIds: string[],
    warehouseLocationId: string
  ): Promise<ShopifyPushSummary> => {
    if (!adminId || productIds.length === 0)
      return { skipped: true, passed: 0, permanentlySkipped: 0, failed: 0 }

    const mappings = await fetchShopifyLocationMappings(adminId)
    const isWarehouseMapped = mappings.some((m) => m.product_location_id === warehouseLocationId)
    if (!isWarehouseMapped)
      return { skipped: true, passed: 0, permanentlySkipped: 0, failed: 0 }

    const baseUrl = import.meta.env.VITE_SUPABASE_URL
    const allIds = Array.from(new Set(productIds))
    const total = allIds.length
    const PUSH_CHUNK = 10
    const REQUEST_TIMEOUT_MS = 70_000
    const MAX_ROUNDS = 4
    const ROUND_BACKOFF_MS = 1_500

    const passed = new Set<string>()
    const permanentlySkipped = new Set<string>()
    let deferredToCron = false

    setShopifySync({ done: 0, total })

    const callPushLocation = async (
      chunk: string[]
    ): Promise<{ results?: Array<{ productId: string; status: string }> } | null> => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
      try {
        const res = await fetch(`${baseUrl}/functions/v1/shopify-push-inventory/push-location`, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ productLocationId: warehouseLocationId, productIds: chunk }),
          signal: controller.signal,
        })
        if (res.status === 403) {
          deferredToCron = true
          return null
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return await res.json()
      } catch (err) {
        console.error('push-location request failed (will retry not-passed SKUs):', err)
        return null
      } finally {
        clearTimeout(timeoutId)
      }
    }

    try {
      for (let round = 0; round < MAX_ROUNDS; round++) {
        if (deferredToCron) break
        const pending = allIds.filter((id) => !passed.has(id) && !permanentlySkipped.has(id))
        if (pending.length === 0) break

        for (let i = 0; i < pending.length; i += PUSH_CHUNK) {
          if (deferredToCron) break
          const chunk = pending.slice(i, i + PUSH_CHUNK)
          const data = await callPushLocation(chunk)

          if (data) {
            const results = Array.isArray(data.results) ? data.results : []
            for (const r of results) {
              if (r.status === 'verified') passed.add(r.productId)
              else if (r.status === 'skipped') permanentlySkipped.add(r.productId)
            }
          }

          setShopifySync({ done: passed.size + permanentlySkipped.size, total })
        }

        const stillPending = allIds.some((id) => !passed.has(id) && !permanentlySkipped.has(id))
        if (!stillPending) break
        if (round < MAX_ROUNDS - 1) await new Promise((r) => setTimeout(r, ROUND_BACKOFF_MS))
      }

      const failed = allIds.filter((id) => !passed.has(id) && !permanentlySkipped.has(id)).length
      return {
        skipped: false,
        passed: passed.size,
        permanentlySkipped: permanentlySkipped.size,
        failed: deferredToCron ? 0 : failed,
        deferredToCron,
      }
    } finally {
      setShopifySync(null)
    }
  }, [adminId])

  const confirmReturn = useCallback(async (args: {
    userId: string
    items: ConfirmReturnItemInput[]
    notes?: string | null
  }): Promise<{
    rpc: ConfirmReturnResult
    push?: ShopifyPushSummary
  }> => {
    if (!adminId) return { rpc: { success: false, error: 'Unable to resolve tenant' } }
    if (!lookup || lookup.status !== 'found' || lookup.shopifyOrderId == null) {
      return { rpc: { success: false, error: 'Look up an order before confirming a return' } }
    }
    if (!args.userId) {
      return { rpc: { success: false, error: 'User session required' } }
    }
    if (!args.items.length) {
      return { rpc: { success: false, error: 'Select at least one item to return' } }
    }

    setConfirming(true)
    try {
      const warehouse = await resolveWarehouseLocation(adminId)
      if (!warehouse) {
        return {
          rpc: {
            success: false,
            error: 'No warehouse location is configured. Please create a "Warehouse" location first.',
          },
        }
      }

      const { data, error: rpcError } = await supabase.rpc('record_order_return_atomic', {
        p_admin_id: adminId,
        p_user_id: args.userId,
        p_shopify_order_id: lookup.shopifyOrderId,
        p_order_number: lookup.orderNumber,
        p_customer_name: lookup.customerName,
        p_shipping_label_id: lookup.shippingLabelId,
        p_scanned_barcode: lookup.scannedBarcode || lookup.scannedValue,
        p_warehouse_location_id: warehouse.id,
        p_items: args.items,
        p_notes: args.notes || null,
      })

      if (rpcError) {
        return { rpc: { success: false, error: rpcError.message } }
      }

      const rpcResult = (data || {}) as ConfirmReturnResult
      if (!rpcResult.success) return { rpc: rpcResult }

      const productIds = (rpcResult.product_ids || []).map(String)
      const outboxIds = (rpcResult.outbox_ids || []).map(String)

      let pushSummary: ShopifyPushSummary | undefined
      const pushPromise = (async () => {
        pushSummary = await pushWarehouseToShopify(productIds, warehouse.id)
        if (
          pushSummary &&
          !pushSummary.skipped &&
          !pushSummary.deferredToCron &&
          pushSummary.failed === 0 &&
          outboxIds.length > 0
        ) {
          await supabase
            .from('shopify_sync_outbox')
            .update({
              status: 'synced',
              last_error: null,
              updated_at: new Date().toISOString(),
            })
            .in('id', outboxIds)
        }
        return pushSummary
      })()

      shopifySyncChainRef.current = shopifySyncChainRef.current
        .catch(() => undefined)
        .then(() => pushPromise)

      pushSummary = await pushPromise.catch(() => undefined)
      return { rpc: rpcResult, push: pushSummary }
    } catch (err) {
      return {
        rpc: { success: false, error: err instanceof Error ? err.message : 'Return failed' },
      }
    } finally {
      setConfirming(false)
    }
  }, [adminId, lookup, pushWarehouseToShopify])

  return {
    loading,
    confirming,
    shopifySync,
    lookup,
    error,
    lookupValue,
    selectCandidate,
    clearLookup,
    confirmReturn,
    setLookup,
  }
}
